/**
 * MissionHistoryRow — Missões timeline historical period row
 *
 * Two visual variants:
 *
 *   - 'day'       → individual past day. Renders as a COLLAPSIBLE block
 *                   (Figma 6313:26671 / 6313:26672). Collapsed shows a
 *                   blue avatar marker, period label, and a Visualizar/Fechar
 *                   toggle. Expanded fetches the day's persisted record via
 *                   `getHistory('day', date)` and renders, on the same
 *                   timeline rail:
 *                     • "Você completou X de N tarefas" + the 5 mission
 *                       rows in their actual completion state (passive,
 *                       no chevrons; completed labels bold).
 *                     • Optional event slots (relapse / supports / posts)
 *                       — gracefully hidden when the API hasn't surfaced
 *                       those fields yet. Server backfill is a follow-up.
 *
 *   - 'aggregate' → period summary ("Semana", "1 Mês", "3 Meses",
 *                   "6 Meses", "12 Meses"). Renders
 *                   as a COLLAPSIBLE block: tappable header + Visualizar/
 *                   Fechar toggle that, on expand, surfaces a Bill-voiced
 *                   AI recap of the period (≤ 500 chars). The row pre-fetches
 *                   on mount to discover availability; if the period has no
 *                   missions logged, the row renders DISABLED — non-tappable,
 *                   muted label, and no Visualizar CTA — so empty periods
 *                   don't lead the user into a dead-end expand.
 *
 * Day rows live INSIDE the timelineWrapper in app/diary.tsx, so the rail
 * threads through both the collapsed header and any expanded sub-rows
 * without extra plumbing.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { TouchableOpacity, View, Text, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Image } from 'expo-image';
import {
  getHistory,
  MISSION_KEYS,
  type MissionKey,
  type RecoveryHistoryResponse,
  type HistoryDayEvents,
  type AiSummary,
} from '@/services/recoveryPlanService';
import MissionRow from '@/components/MissionRow';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import RevenueCatService from '@/services/revenueCatService';

// Hard cap on the AI recap shown in aggregate rows. Mirrors the spec
// ("max 500 characters text"). Any longer payload from a future Bill-voiced
// generator is truncated client-side with an ellipsis so layout stays bounded.
const AI_SUMMARY_MAX_CHARS = 500;

function truncate(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, Math.max(0, n - 1)).trimEnd() + '…';
}

// Sum the per-mission counts in an AiSummary. Used to decide whether an
// aggregate period has anything worth surfacing — when the sum is 0 the
// row renders as disabled and is not expandable.
function totalsSum(summary?: AiSummary | null): number {
  if (!summary?.totals) return 0;
  return Object.values(summary.totals).reduce<number>(
    (acc, v) => acc + (typeof v === 'number' ? v : 0),
    0,
  );
}

type AggregateKey = 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'twelveMonths';

interface DayProps {
  variant: 'day';
  /** Pre-formatted, locale-correct label, e.g. "Ontem, 28 de Fevereiro" */
  label: string;
  /** ISO date YYYY-MM-DD passed to the period viewer */
  date: string;
  isLast?: boolean;
}

interface AggregateProps {
  variant: 'aggregate';
  labelKey: AggregateKey;
  isLast?: boolean;
}

type Props = DayProps | AggregateProps;

const PERIOD_FOR_AGGREGATE: Record<
  AggregateKey,
  'week' | 'month' | 'quarter' | 'halfyear' | 'year'
> = {
  week: 'week',
  month: 'month',
  threeMonths: 'quarter',
  sixMonths: 'halfyear',
  twelveMonths: 'year',
};

// ---------------------------------------------------------------------------
// Day record — shape the UI consumes from the history API. Optional event
// fields are FORWARD-COMPATIBLE: they render gracefully when undefined and
// will light up once the server backfills them. Tracked as a follow-up.
// ---------------------------------------------------------------------------
type DayRecord = {
  date: string;
  missions: Record<MissionKey, boolean>;
  completedCount: number;
} & HistoryDayEvents;

// ---------- Time-ago formatter (compact, locale-agnostic numbers) ---------
function formatTimeAgo(iso: string, t: (k: string, p?: any) => string): string {
  const created = new Date(iso).getTime();
  if (!Number.isFinite(created)) return '';
  const diffMs = Date.now() - created;
  const minutes = Math.max(1, Math.floor(diffMs / 60_000));
  if (minutes < 60) return t('missoes.history.timeAgo.minute', { n: String(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('missoes.history.timeAgo.hour', { n: String(hours) });
  const days = Math.floor(hours / 24);
  return t('missoes.history.timeAgo.day', { n: String(days) });
}

// ---------- Initials helper for avatar fallback ---------------------------
function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

// ---------- Tiny SVG glyphs ------------------------------------------------

// 4-burst star — Bege Cararra stroke on the blue period avatar.
const BurstStar = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M7.83984 1.84863V13.6887" stroke="#F0EEE4" strokeWidth="2" strokeLinecap="square" />
    <Path d="M13.7599 7.76855H1.91992" stroke="#F0EEE4" strokeWidth="2" strokeLinecap="square" />
    <Path d="M12.0264 3.58301L3.6543 11.9551" stroke="#F0EEE4" strokeWidth="2" strokeLinecap="square" />
    <Path d="M12.0264 11.9547L3.6543 3.58252" stroke="#F0EEE4" strokeWidth="2" strokeLinecap="square" />
  </Svg>
);

// Diamond/gem glyph used as the Plus paywall affordance on aggregate rows
// (week / month / 12 months) when the user is NOT on Padrinho Plus. Same
// shape as the Figma "Gem_605_2086" spec at 24×24 / Blue Horizon stroke,
// kept inline (vs. a shared component) to avoid coupling MissionHistoryRow
// to TrailCard's local helpers — the icon is small enough that duplication
// is cheaper than a new shared module.
const GemIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M3.51047 11.5274L10.8163 19.5542C11.4507 20.2518 12.5482 20.2518 13.1825 19.5542L20.4894 11.5264C21.1004 10.8561 21.1704 9.85395 20.6596 9.10381L17.7174 4.78197C17.3526 4.24685 16.7455 3.92578 16.0975 3.92578H7.91111C7.26312 3.92578 6.65698 4.24588 6.29212 4.781L3.34117 9.10381C2.8294 9.85298 2.89946 10.8561 3.51047 11.5274Z"
      stroke="#669AB7"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M13.9989 3.92578L15.3659 9.10381C15.5643 9.85395 15.5371 10.8561 15.2997 11.5264L12.4597 19.5542C12.2135 20.2518 11.7864 20.2518 11.5402 19.5542L8.7002 11.5274C8.4628 10.8561 8.43556 9.85298 8.63501 9.10381L10.0059 3.92578"
      stroke="#669AB7"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M3.00781 10.2031H20.9908"
      stroke="#669AB7"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Double chevron for the Visualizar / Fechar toggle.
//   - open=false (Visualizar): chevrons SPREAD apart (⌃ over ⌄) → "expand"
//   - open=true  (Fechar):     chevrons CONVERGE       (⌄ over ⌃) → "collapse"
const ToggleChevron = ({ open }: { open: boolean }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    {open ? (
      <>
        {/* Top chevron points DOWN, bottom points UP — converge to middle */}
        <Path
          d="M8 7L12 11L16 7"
          stroke="#669AB7"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <Path
          d="M16 17L12 13L8 17"
          stroke="#669AB7"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </>
    ) : (
      <>
        {/* Top chevron points UP, bottom points DOWN — spread outward */}
        <Path
          d="M8 11L12 7L16 11"
          stroke="#669AB7"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <Path
          d="M8 13L12 17L16 13"
          stroke="#669AB7"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </>
    )}
  </Svg>
);

// ---------------------------------------------------------------------------
// Aggregate variant — collapsible. On expand surfaces the period AI recap
// (max 500 chars). Pre-fetches on mount so empty periods can render disabled
// (non-tappable, no Visualizar CTA).
//
// `availability` lifecycle:
//   'unknown'  → first render, before the pre-fetch resolves. Row is
//                tappable; tap kicks off (or reuses cached) fetch.
//   'has_data' → fetch returned aiSummary with totals > 0. Tap toggles
//                the inline summary block.
//   'no_data'  → fetch returned no missions in the period. Row is fully
//                disabled — muted label, no CTA, no chevron, no onPress.
// ---------------------------------------------------------------------------
function AggregateRow(props: AggregateProps) {
  const { t } = useLanguage();
  const period = PERIOD_FOR_AGGREGATE[props.labelKey];
  const label = t(`missoes.history.${props.labelKey}`);

  // Plus gating — week / month / 12 months periods are Plus-only. Free
  // users see a "Plus + 💎" affordance on enabled rows that opens the
  // RevenueCat paywall on tap; no body content is ever rendered for them
  // (so they can't peek at the AI recap by toggling state mid-fetch).
  //
  // We use `useServerVerifiedPlus` (NOT the RevenueCat-only client hook)
  // because Plus access can come from sources other than the App Store /
  // Play Store SDK — admin overrides, server-side promo grants, the
  // `is_plus_user` profile flag — and the server endpoint is the
  // authoritative source of truth that consolidates all of them. Every
  // other gated surface in the app (perfil, subscription, community,
  // sponsors, trail, home) uses the same hook, so behaviour is consistent.
  //
  // While entitlement is loading we render the standard toggle so we
  // never *downgrade* a Plus user mid-session — the paywall path opens
  // only when we have a confirmed `false`.
  const { hasPadrinhoPlus, isLoading: plusLoading } = useServerVerifiedPlus();
  const isPlusGated = !plusLoading && !hasPadrinhoPlus;

  const [availability, setAvailability] = useState<'unknown' | 'has_data' | 'no_data'>('unknown');
  const [isOpen, setIsOpen] = useState(false);
  const [summary, setSummary] = useState<AiSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(
    async (opts?: { background?: boolean; force?: boolean }) => {
      if (!opts?.background) setLoading(true);
      setError(null);
      try {
        const res = await getHistory(period, undefined, { force: opts?.force });
        if (!('ok' in res) || !res.ok) {
          if (!opts?.background) setError(t('missoes.history.loadFailed'));
          return;
        }
        const r = res as RecoveryHistoryResponse;
        const ai = r.aiSummary ?? null;
        setSummary(ai);
        // Decide availability from BOTH the summary totals AND the raw day
        // records. The server downgrades aiSummary to null on generator
        // failure (caught try/catch in fetchHistory), but the days array can
        // still contain real completions — disabling in that case would
        // wrongly hide a tappable period. Belt-and-suspenders: any non-zero
        // signal from either source means we have something to show.
        //
        // Per-day count fallback: prefer the persisted completedCount, but
        // if it's missing/0 (older server payloads where snake→camel
        // mapping wasn't applied yet), recompute from the missions object
        // so the count works even before a backend redeploy.
        const dayMissionSum = (r.days ?? []).reduce<number>((acc, d) => {
          const persisted = typeof d.completedCount === 'number' ? d.completedCount : 0;
          if (persisted > 0) return acc + persisted;
          const recomputed = MISSION_KEYS.reduce(
            (n, k) => n + (d.missions?.[k] ? 1 : 0),
            0,
          );
          return acc + recomputed;
        }, 0);
        const hasData = totalsSum(ai) > 0 || dayMissionSum > 0;
        setAvailability(hasData ? 'has_data' : 'no_data');
        // If the user happened to expand while we were still 'unknown' and
        // the resolved state is 'no_data', collapse so the rendered tree
        // matches the now-disabled header (a11y `expanded` stays consistent).
        if (!hasData) setIsOpen(false);
      } catch {
        if (!opts?.background) setError(t('missoes.history.loadFailed'));
      } finally {
        if (!opts?.background) setLoading(false);
      }
    },
    [period, t],
  );

  // Pre-fetch on mount so disabled state lights up before any tap.
  // We FORCE a refresh here (bypassing the 1-hour client cache) because
  // production builds keep the JS process alive across app
  // background/foreground cycles — the in-memory cache would otherwise
  // serve a stale summary indefinitely after a server-side change. The
  // /history endpoint is cheap (single supabase read + an optional Bill
  // call when expanded), so the trade-off is fine.
  useEffect(() => {
    fetchSummary({ background: true, force: true });
  }, [fetchSummary]);

  const isDisabled = availability === 'no_data';

  const handlePresentPaywall = useCallback(async () => {
    try {
      await RevenueCatService.presentPaywall({ fromSurface: 'mission_history' });
      // Subscription state is broadcast via DeviceEventEmitter
      // ('subscription:status-changed') by the post-purchase sync flow,
      // and useServerVerifiedPlus listens for it — so a successful
      // purchase will flip this row out of paywall mode automatically.
      // Nothing else to do here.
    } catch (e) {
      console.warn('⚠️ [MissionHistoryRow] paywall present failed:', e);
    }
  }, []);

  const handleToggle = () => {
    if (isDisabled) return;
    // Free users never expand — tapping the row opens the paywall.
    if (isPlusGated) {
      handlePresentPaywall();
      return;
    }
    const next = !isOpen;
    setIsOpen(next);
    // Every time the user taps "Visualizar" (opening the row), re-fetch
    // with force=true so the Bill feedback evaluation is re-run fresh.
    // This bypasses the 1-hour client cache and ensures the user always
    // gets the latest AI recap on each tap.
    if (next) fetchSummary({ force: true });
  };

  const cta = isOpen ? t('missoes.history.close') : t('missoes.history.view');
  const summaryText = summary?.text ? truncate(summary.text, AI_SUMMARY_MAX_CHARS) : '';

  return (
    <View style={[styles.dayBlock, !props.isLast && styles.aggregateSpacing]}>
      <TouchableOpacity
        style={styles.headerRow}
        onPress={handleToggle}
        activeOpacity={isDisabled ? 1 : 0.7}
        disabled={isDisabled}
        accessibilityRole={isDisabled ? 'text' : 'button'}
        accessibilityState={{ expanded: isOpen, disabled: isDisabled }}
        accessibilityLabel={
          isDisabled
            ? label
            : isPlusGated
              ? `${label} — Plus`
              : `${label} — ${cta}`
        }
      >
        <View style={styles.markerColumn}>
          {/* Marker swap based on availability:
              - has_data → blue avatar + burst star (matches Figma 6337:14726
                and the day-row marker, so populated periods read as
                "openable milestones").
              - no_data / unknown → minimal black/grey dot. The 'unknown'
                state stays understated to avoid a jarring marker flip if
                the period later resolves to empty. */}
          {availability === 'has_data' ? (
            <View style={styles.periodAvatar}>
              <BurstStar />
            </View>
          ) : (
            <View style={[styles.aggregateDot, isDisabled && styles.aggregateDotDisabled]} />
          )}
        </View>
        <Text
          style={[styles.aggregateLabel, isDisabled && styles.aggregateLabelDisabled]}
          numberOfLines={1}
        >
          {label}
        </Text>
        {!isDisabled && (
          <View style={styles.toggle}>
            <Text style={styles.toggleText}>{isPlusGated ? 'Plus' : cta}</Text>
            {isPlusGated ? <GemIcon /> : <ToggleChevron open={isOpen} />}
          </View>
        )}
      </TouchableOpacity>

      {isOpen && !isDisabled && !isPlusGated && (
        <View style={styles.expanded}>
          {loading && (
            <View style={styles.subRow}>
              <View style={styles.markerColumn}>
                <View style={styles.subRowDot} />
              </View>
              <Text style={styles.metaText}>{t('missoes.history.loading')}</Text>
            </View>
          )}
          {!loading && error && (
            <View style={styles.subRow}>
              <View style={styles.markerColumn}>
                <View style={styles.subRowDot} />
              </View>
              <Text style={styles.metaText}>{error}</Text>
            </View>
          )}
          {!loading && !error && !!summaryText && (
            <View style={styles.subRow}>
              <View style={styles.markerColumn}>
                <View style={styles.subRowDot} />
              </View>
              <View style={styles.subRowContent}>
                <Text style={styles.aiSummaryText}>{summaryText}</Text>
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Day variant — collapsible.
// ---------------------------------------------------------------------------
function DayRow(props: DayProps) {
  const { t } = useLanguage();
  const { userProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [day, setDay] = useState<DayRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'unknown' while the background pre-fetch is in flight; resolves to
  // 'has_data' (a persisted row exists) or 'no_data' (no row for this day).
  // no_data → row renders disabled, identical to Semana / Mês / 12 meses.
  const [availability, setAvailability] = useState<'unknown' | 'has_data' | 'no_data'>('unknown');

  // The supports sub-row is "you supported N people" — the avatar should
  // be the SELF user, not the recipient. Pulled from the authed profile
  // so initials still render via the Avatar fallback when no photo is set.
  const selfAvatarUrl = userProfile?.avatar_url ?? null;
  const selfName = userProfile?.full_name ?? null;

  // Pre-fetch availability on mount (background, no UI loading state).
  // Mirrors the AggregateRow pattern: know whether data exists before the
  // user taps, so no-data days render disabled from the start instead of
  // showing an error only after the user tries to expand.
  const prefetchAvailability = useCallback(async () => {
    try {
      const res = await getHistory('day', props.date);
      if (!('ok' in res) || !res.ok) {
        // Server/network error — keep 'unknown' so the row stays tappable.
        // The error will surface only if the user actually tries to expand.
        return;
      }
      const r = res as RecoveryHistoryResponse;
      const match = r.days.find((d) => d.date === props.date) || r.days[0];
      setAvailability(match ? 'has_data' : 'no_data');
    } catch {
      // Network error — keep 'unknown' (safe fallback, same as above).
    }
  }, [props.date]);

  useEffect(() => {
    prefetchAvailability();
  }, [prefetchAvailability]);

  const isDisabled = availability === 'no_data';

  const fetchDay = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getHistory('day', props.date);
      if (!('ok' in res) || !res.ok) {
        setError(t('missoes.history.loadFailed'));
        return;
      }
      const r = res as RecoveryHistoryResponse;
      const match = r.days.find((d) => d.date === props.date) || r.days[0];
      if (!match) {
        // No persisted record for this day (predates tracking or user was
        // inactive and the nightly cron skipped it). Show a clean 0-of-5
        // empty state instead of an error — "loadFailed" is reserved for
        // real network / server failures caught in the catch block below.
        const emptyMissions = Object.fromEntries(
          MISSION_KEYS.map((k) => [k, false]),
        ) as Record<MissionKey, boolean>;
        setDay({ date: props.date, missions: emptyMissions, completedCount: 0 });
        return;
      }
      // Shape-cast: optional event fields will be present once the server
      // backfill ships; until then they stay undefined and the corresponding
      // sub-rows simply don't render.
      setDay(match as DayRecord);
    } catch (e) {
      setError(t('missoes.history.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [props.date, t]);

  const handleToggle = () => {
    if (isDisabled) return;
    const next = !isOpen;
    setIsOpen(next);
    if (next && !day && !loading) fetchDay();
  };

  const cta = isOpen ? t('missoes.history.close') : t('missoes.history.view');

  return (
    <View style={[styles.dayBlock, !props.isLast && styles.dayBlockSpacing]}>
      {/* Collapsed header — always visible, anchored to the rail. */}
      <TouchableOpacity
        style={styles.headerRow}
        onPress={handleToggle}
        activeOpacity={isDisabled ? 1 : 0.7}
        disabled={isDisabled}
        accessibilityRole={isDisabled ? 'text' : 'button'}
        accessibilityState={{ expanded: isOpen, disabled: isDisabled }}
        accessibilityLabel={isDisabled ? props.label : `${props.label} — ${cta}`}
      >
        <View style={styles.markerColumn}>
          {!isDisabled ? (
            <View style={styles.periodAvatar}>
              <BurstStar />
            </View>
          ) : (
            <View style={[styles.aggregateDot, styles.aggregateDotDisabled]} />
          )}
        </View>
        <Text
          style={[styles.periodLabel, isDisabled && styles.aggregateLabelDisabled]}
          numberOfLines={1}
        >
          {props.label}
        </Text>
        {!isDisabled && (
          <View style={styles.toggle}>
            <Text style={styles.toggleText}>{cta}</Text>
            <ToggleChevron open={isOpen} />
          </View>
        )}
      </TouchableOpacity>

      {/* Expanded content — additional rail-anchored sub-rows. */}
      {isOpen && !isDisabled && (
        <View style={styles.expanded}>
          {loading && (
            <View style={styles.subRow}>
              <View style={styles.markerColumn}>
                <View style={styles.subRowDot} />
              </View>
              <Text style={styles.metaText}>{t('missoes.history.loading')}</Text>
            </View>
          )}

          {!loading && error && (
            <View style={styles.subRow}>
              <View style={styles.markerColumn}>
                <View style={styles.subRowDot} />
              </View>
              <Text style={styles.metaText}>{error}</Text>
            </View>
          )}

          {!loading && !error && day && (
            <>
              {/* Missions sub-row */}
              <View style={styles.subRow}>
                <View style={styles.markerColumn}>
                  <View style={styles.subRowDot} />
                </View>
                <View style={styles.subRowContent}>
                  <Text style={styles.summaryHeading}>
                    {t('missoes.history.completedSummary', {
                      // Prefer the persisted completedCount. If it's missing
                      // or 0 (older server payloads where snake→camel mapping
                      // wasn't applied), recompute from the missions object
                      // so the UI shows the real count even before the
                      // backend redeploy reaches this device.
                      done: String(
                        day.completedCount && day.completedCount > 0
                          ? day.completedCount
                          : MISSION_KEYS.reduce(
                              (n, k) => n + (day.missions?.[k] ? 1 : 0),
                              0,
                            ),
                      ),
                      total: String(MISSION_KEYS.length),
                    })}
                  </Text>
                  {MISSION_KEYS.map((key, idx) => (
                    <MissionRow
                      key={key}
                      missionKey={key}
                      completed={!!day.missions[key]}
                      isLast={idx === MISSION_KEYS.length - 1}
                      historical
                    />
                  ))}
                </View>
              </View>

              {/* Relapse sub-row — orange dot + warning copy */}
              {day.hadRelapse && (
                <View style={styles.subRow}>
                  <View style={styles.markerColumn}>
                    <View style={[styles.subRowDot, styles.subRowDotRelapse]} />
                  </View>
                  <Text style={styles.relapseText}>
                    {t('missoes.history.relapseMessage')}
                  </Text>
                </View>
              )}

              {/* Supports sub-row — the avatar belongs to the SELF user
                  (it was *their* support action), and the copy is fully
                  count-driven so the verb agrees with the number:
                  "1 pessoa recebeu" vs "N pessoas receberam". */}
              {!!day.supportsCount && day.supportsCount > 0 && (
                <View style={styles.subRow}>
                  <View style={styles.markerColumn}>
                    <Avatar uri={selfAvatarUrl} name={selfName} />
                  </View>
                  <Text style={styles.metaText}>
                    {/* "1 pessoa" / "{count} pessoas" rendered bold; the
                        verb-agreeing suffix stays in the regular weight. */}
                    <Text style={styles.metaTextBold}>
                      {day.supportsCount === 1
                        ? t('missoes.history.supports.one.bold')
                        : t('missoes.history.supports.many.bold', {
                            count: String(day.supportsCount),
                          })}
                    </Text>
                    {day.supportsCount === 1
                      ? t('missoes.history.supports.one.suffix')
                      : t('missoes.history.supports.many.suffix')}
                  </Text>
                </View>
              )}

              {/* Posts sub-row — single post renders the full testimony card;
                  multi-post case falls back to the aggregated count copy. */}
              {day.postsCount === 1 && day.latestPost && (
                <View style={styles.subRow}>
                  <View style={styles.markerColumn}>
                    <Avatar
                      uri={
                        day.latestPost.isAnonymous
                          ? null
                          : day.latestPost.author?.avatarUrl ?? selfAvatarUrl
                      }
                      name={
                        day.latestPost.isAnonymous
                          ? t('missoes.history.posts.anonymous')
                          : day.latestPost.author?.name ?? selfName
                      }
                    />
                  </View>
                  <View style={styles.testimonyCard}>
                    <View style={styles.testimonyHeader}>
                      <Text style={styles.testimonyAuthor} numberOfLines={1}>
                        {day.latestPost.isAnonymous
                          ? t('missoes.history.posts.anonymous')
                          : day.latestPost.author?.name ?? selfName ?? t('missoes.history.posts.you')}
                      </Text>
                      <Text style={styles.testimonyTime}>
                        {formatTimeAgo(day.latestPost.createdAt, t)}
                      </Text>
                    </View>
                    <Text style={styles.testimonyBody} numberOfLines={6}>
                      {day.latestPost.content}
                    </Text>
                  </View>
                </View>
              )}
              {!!day.postsCount && day.postsCount > 1 && (
                <View style={styles.subRow}>
                  <View style={styles.markerColumn}>
                    <Avatar
                      uri={
                        day.latestPost?.isAnonymous
                          ? null
                          : day.latestPost?.author?.avatarUrl ?? null
                      }
                      name={
                        day.latestPost?.isAnonymous
                          ? t('missoes.history.posts.anonymous')
                          : day.latestPost?.author?.name
                      }
                    />
                  </View>
                  <Text style={styles.metaText}>
                    {t('missoes.history.posts.many', {
                      count: String(day.postsCount),
                    })}
                  </Text>
                </View>
              )}
            </>
          )}
        </View>
      )}
    </View>
  );
}

// ---------- Avatar (uri w/ initials fallback) -----------------------------
function Avatar({ uri, name }: { uri?: string | null; name?: string | null }) {
  if (uri) {
    return (
      <Image
        source={{ uri }}
        style={styles.smallAvatarImg}
        contentFit="cover"
        transition={120}
      />
    );
  }
  return (
    <View style={styles.smallAvatar}>
      <Text style={styles.smallAvatarText}>{initials(name)}</Text>
    </View>
  );
}

// ---------------------------------------------------------------------------

export default function MissionHistoryRow(props: Props) {
  if (props.variant === 'aggregate') {
    return <AggregateRow {...props} />;
  }
  return <DayRow {...props} />;
}

// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  dayBlock: {
    width: '100%',
  },
  dayBlockSpacing: {
    marginBottom: 4,
  },
  // Generic row shell — mirrors `timelineRow` in app/diary.tsx so every
  // marker (period avatar, dot, mini-avatar) lands on the same rail line.
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 8,
    width: '100%',
  },
  markerColumn: {
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Blue period avatar (32×32, Blue Horizon, holds the burst star).
  periodAvatar: {
    width: 32,
    height: 32,
    borderRadius: 20,
    backgroundColor: '#669AB7',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
  },
  periodLabel: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  toggleText: {
    color: '#669AB7',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 14,
  },

  // Expanded content
  expanded: {
    width: '100%',
    paddingBottom: 24,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    paddingTop: 8,
    width: '100%',
  },
  subRowContent: {
    flex: 1,
    minWidth: 0,
    gap: 12,
    paddingBottom: 16,
  },
  // Small inline marker — 8×8 square, mid-marker-column.
  subRowDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#151515',
    marginTop: 4,
  },
  subRowDotRelapse: {
    backgroundColor: '#DE5609',
  },
  // Mini circular avatar fallback for support / posts rows.
  smallAvatar: {
    width: 32,
    height: 32,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    backgroundColor: '#015480',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallAvatarText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    fontWeight: '700',
  },
  smallAvatarImg: {
    width: 32,
    height: 32,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    backgroundColor: '#015480',
  },
  // Single-post testimony — flat layout per Figma (Testimony spec).
  // No card chrome: just avatar column + name/time header row + body text.
  testimonyCard: {
    flex: 1,
    gap: 4,
  },
  testimonyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  testimonyAuthor: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  testimonyTime: {
    color: '#757C85',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 12,
    textAlign: 'right',
  },
  testimonyBody: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  summaryHeading: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  metaText: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    paddingTop: 4,
  },
  // Inline-bold span for the count fragment ("1 pessoa" / "{n} pessoas").
  metaTextBold: {
    fontFamily: 'Inter_700Bold',
    fontWeight: '700',
  },
  relapseText: {
    flex: 1,
    color: '#DE5609',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    paddingTop: 4,
  },

  // Aggregate variant
  aggregateSpacing: {
    marginBottom: 4,
  },
  aggregateDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#151515',
  },
  // Disabled state — Concord (#7E7D75) for both the dot and the label.
  // Mirrors the muted "12 meses" preview in the Figma empty-state.
  aggregateDotDisabled: {
    backgroundColor: '#7E7D75',
  },
  aggregateLabel: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
  aggregateLabelDisabled: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontWeight: '500',
  },
  // AI period recap (≤ 500 chars). Same body type as testimony copy so
  // the expanded block reads as a continuation of the timeline rail.
  aiSummaryText: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
});
